import axios from 'axios';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Single chat message attached to a contact request
 */
export interface ChatMessagePayload {
  author: 'assistant' | 'client';
  step?: string;
  content: string;
  createdAt?: string;
}

/**
 * Contact Request payload for chatbot submissions
 */
export interface CreateContactRequestPayload {
  propertyId: string;
  seekerName: string;
  seekerEmail: string;
  seekerPhone: string;
  seekerMessage: string;
  budgetMin?: number;
  budgetMax?: number;
  moveInDate?: string;
  source: 'chatbot' | 'form' | 'website';
  messages?: ChatMessagePayload[];
}

/**
 * Contact Request response from API
 */
export interface ContactRequestResponse {
  id: string;
  propertyId: string;
  seekerName: string;
  seekerEmail: string;
  seekerPhone: string;
  seekerMessage: string;
  budgetMin?: number;
  budgetMax?: number;
  moveInDate?: string;
  // TODO: Réactiver quand la logique de score sera implémentée
  // score: number;
  // priority: 'HIGH' | 'MEDIUM' | 'LOW';
  status: string;
  createdAt: string;
}

/**
 * Existing contact request with messages (for chatbot restoration)
 */
export interface ExistingContactRequest {
  id: string;
  propertyId: string;
  seekerName: string;
  seekerEmail: string;
  seekerPhone: string;
  seekerMessage: string;
  budgetMin?: number;
  budgetMax?: number;
  moveInDate?: string;
  status: string;
  createdAt: string;
  messages?: Array<{
    id: string;
    author: 'ASSISTANT' | 'CLIENT';
    step?: string;
    content: string;
    createdAt: string;
  }>;
}

/**
 * Service for contact request operations
 */
export const contactRequestService = {
  /**
   * Creates a new contact request from the chatbot conversation
   */
  create: async (payload: CreateContactRequestPayload): Promise<ContactRequestResponse> => {
    try {
      const response = await axios.post<ContactRequestResponse>(
        `${API}/contact-requests`,
        payload
      );
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      throw new Error(message);
    }
  },

  /**
   * Get existing contact request for a property (for chatbot restoration)
   */
  getExistingByProperty: async (propertyId: string): Promise<ExistingContactRequest | null> => {
    try {
      const response = await axios.get<{ request: ExistingContactRequest | null }>(
        `${API}/client/contact-request/by-property/${propertyId}`
      );
      return response.data.request;
    } catch (error) {
      // If 404 or not found, return null (no existing request)
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      // For other errors, log but return null to allow new conversation
      console.error('Error fetching existing contact request:', error);
      return null;
    }
  },
};

export default contactRequestService;

